import { Injectable } from '@angular/core';
import { HttpService } from "./http.service";
import { CreateResult } from "../models/create-result";
import { TerminalConfigViewModel } from "../models/terminal-config-view-model";
import { CountryViewModel } from '../models/country-view-model';
import { SupportTeamsViewModel } from '../models/support-teams-model';
import { TermDriverIDViewModel } from '../models/term-driver-id-model';
import { TimeZoneCodeViewModel } from '../models/time-zone-code-view-model';
import { BinProfilesViewModel } from '../models/bin-profile-view-model';
import { TerminalTypeViewModel } from '../models/terminal-type-view-model';
import {TerminalDetailModel} from "../models/terminal-detail-model";
import { BehaviorSubject, Observable } from 'rxjs';
import { PagedResponse } from '../models/paged-response';
import { SearchFilterModel } from '../models/search-filter-model';
import { MerchantTerminalConfigViewModel } from '../models/merchant-terminal-config-view-model';
import { RetailerConfigViewModel } from '../models/terminal-config-retailer-view-model';
import { StoreViewModel } from '../models/store-view-model';
import { ConfigSetViewModel } from '../models/config-set-view-model';
import { RolloutGroupViewModel } from '../models/rollout-group-view-model';
import { BranchViewModel } from '../models/branch-view-model';
import { LaneProfilesViewModel } from '../models/lane-profiles-view-model';
import { AdditionalEsocketConfig } from '../models/additional-esocket-config-view-model';
import { TerminalProfilesViewModel } from '../models/terminal-profiles-view-model';
import { PropertyProfileViewModel } from '../models/property-profile-view-model';
import { RegionConfigViewModel } from '../models/region-config-view-model';
import { AidClassViewModel } from '../models/aid-class-view-model';
import { CardSetConfigServerViewModel } from '../models/card-set-config-server-view-model';
import { CountryConfigViewModel } from '../models/country-config-view-model';

@Injectable({
  providedIn: 'root'
})
export class TerminalConfigService {

  private countSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public get count(): number {
    return this.countSubject.value;
  }

  public set count(value) {
    this.countSubject.next(value);
  }

  public get countObservable(): Observable<number> {
    return this.countSubject.asObservable();
  }

  constructor(private _httpService: HttpService) {
    this.InitTermConfigCount();
  }

  public async Get(configId: string): Promise<TerminalConfigViewModel> {
    const httpResult = await this._httpService.apiGet<TerminalConfigViewModel>(`terminalConfig/${configId}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return null;
  }

  public async GetDefaultConfigByParticipantId(participantId: number): Promise<MerchantTerminalConfigViewModel> {
    const httpResult = await this._httpService.apiGet<MerchantTerminalConfigViewModel>(`terminalConfig/terminalConfigByParticipantId/${participantId}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return null;
  }

  public async GetAllTerminalDetails(configId: number): Promise<TerminalDetailModel[]> {
    const httpResult = await this._httpService.apiGet<TerminalDetailModel[]>(`terminalConfig/TerminalDetails/${configId}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async InitTermConfigCount(): Promise<void> {
    this.countSubject = new BehaviorSubject<number>(0);
    const httpResult = await this._httpService.apiGet<number>(`terminalConfig/count`);
    if (httpResult && httpResult.body) {
      this.count = httpResult.body;
    }
  }

  public async GetTerminalDetail(id: string): Promise<TerminalDetailModel> {
    const httpResult = await this._httpService.apiGet<TerminalDetailModel>(`terminalConfig/TerminalDetail/${id}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return null;
  }

  public async GetAllConfigs(): Promise<TerminalConfigViewModel[]> {
    const httpResult = await this._httpService.apiGet<TerminalConfigViewModel[]>(`terminalConfig/All`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetAllConfigsByParticipantId(participantId: number): Promise<TerminalConfigViewModel[]> {
    const httpResult = await this._httpService.apiGet<TerminalConfigViewModel[]>(`terminalConfig/AllByParticipantId/${participantId}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetConfigsByParticipantId(participantId: number): Promise<TerminalConfigViewModel[]> {
    const httpResult = await this._httpService.apiGet<TerminalConfigViewModel[]>(`terminalConfig/All/${participantId}`);
    if (httpResult && httpResult.body) {
      if (httpResult && httpResult.body) {
        return httpResult.body;
      }
    }
    return [];
  }

  public async GetAllMerchantTerminalsConfigs(): Promise<MerchantTerminalConfigViewModel[]> {
    const httpResult = await this._httpService.apiGet<MerchantTerminalConfigViewModel[]>(`terminalConfig/AllMerchantTerminalConfig`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }


  public async GetPagedConfigs(filter: SearchFilterModel): Promise<PagedResponse<TerminalConfigViewModel>> {
    const httpResult = await this._httpService.apiPost<PagedResponse<TerminalConfigViewModel>>(`terminalConfig/paged`, filter);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return {
      response: null,
      total: 0
    };
  }

  public async Update(terminalConfig: TerminalConfigViewModel): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`terminalConfig/${terminalConfig.id}`, terminalConfig);
    if (httpResult && httpResult.body) {
      this.InitTermConfigCount();
      return httpResult.body;
    } else if (httpResult.error["status"] == 403) {
      return { message: "You do not have the permission to update a terminal config. Please contact system administrator.", success: false }
    }
    return { message: "An unknown error occured", success: false };
  }

  public async Add(configViewModel: TerminalConfigViewModel): Promise<CreateResult> {
    let functionVector: boolean = Boolean(configViewModel.functionVector);
    configViewModel.functionVector = Boolean(functionVector);

    const httpResult = await this._httpService.apiPost<CreateResult>(`terminalConfig/Add`, configViewModel);
    if (httpResult && httpResult.body) {
      this.InitTermConfigCount();
      return httpResult.body;
    } else if (httpResult.error["status"] == 403) {
      return { message: "You do not have the permission to add terminal config. Please contact system administrator.", success: false }
    }
    return { message: "An unknown error occured", success: false };
  }

  public async AddMerchantTerminalConfig(configViewModel: MerchantTerminalConfigViewModel): Promise<CreateResult> {
   
    const httpResult = await this._httpService.apiPost<CreateResult>(`terminalConfig/AddTerminalnMerchantConfig`, configViewModel);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    else if (httpResult.error["status"] == 403) {
      return { message: "You do not have the permission to add terminal config. Please contact system administrator.", success: false }
    }
    return { message: "An unknown error occured", success: false };
  }

  public async SyncRetailer(retailerViewModel: RetailerConfigViewModel): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`terminalConfig/ESocket/SyncRetailer`, retailerViewModel);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    } else if (httpResult.error["status"] == 403) {
      return { message: "You do not have the permission to update a terminal config. Please contact system administrator.", success: false }
    }
    return { message: "An unknown error occured", success: false };
  }

  public async CreateRetailer(retailerViewModel: RetailerConfigViewModel): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`terminalConfig/ESocket/Retailer`, retailerViewModel);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    } else if (httpResult.error["status"] == 403) {
      return { message: "You do not have the permission to update a terminal config. Please contact system administrator.", success: false }
    }
    return { message: "An unknown error occured", success: false };
  }

  public async UpdateRetailer(retailerViewModel: RetailerConfigViewModel): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPut<CreateResult>(`terminalConfig/ESocket/Retailer`, retailerViewModel);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    } else if (httpResult.error["status"] == 403) {
      return { message: "You do not have the permission to update a terminal config. Please contact system administrator.", success: false }
    }
    return { message: "An unknown error occured", success: false };
  }

  public async SyncConfigSet(configSetViewModel: ConfigSetViewModel): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`terminalConfig/ESocket/ConfigSet`, configSetViewModel);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    } else if (httpResult.error["status"] == 403) {
      return { message: "You do not have the permission to update a terminal config. Please contact system administrator.", success: false }
    }
    return { message: "An unknown error occured", success: false };
  }

  public async SyncStore(storeViewModel: StoreViewModel): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`terminalConfig/ESocket/Store`, storeViewModel);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    } else if (httpResult.error["status"] == 403) {
      return { message: "You do not have the permission to update a terminal config. Please contact system administrator.", success: false }
    }
    return { message: "An unknown error occured", success: false };
  } 

  public async CreateRollOutGroup(rolloutGroupViewModel: RolloutGroupViewModel): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`terminalConfig/ESocket/RolloutGroup`, rolloutGroupViewModel);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    } else if (httpResult.error["status"] == 403) {
      return { message: "You do not have the permission to update a terminal config. Please contact system administrator.", success: false }
    }
    return { message: "An unknown error occured", success: false };
  }

  public async SyncLaneProfile(laneProfilesViewModel: LaneProfilesViewModel): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`terminalConfig/ESocket/LaneProfile`, laneProfilesViewModel);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    } else if (httpResult.error["status"] == 403) {
      return { message: "You do not have the permission to update a terminal config. Please contact system administrator.", success: false }
    }
    return { message: "An unknown error occured", success: false };
  } 

  public async CreateUpdateAdditionalEsocketConfig(additionalESocketConfigViewModel: AdditionalEsocketConfig): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`terminalConfig/ESocket/AdditionalESocketConfig`, additionalESocketConfigViewModel);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    } else if (httpResult.error["status"] == 403) {
      return { message: "You do not have the permission to update a terminal config. Please contact system administrator.", success: false }
    }
    return { message: "An unknown error occured", success: false };
  }

  public async CreateUpdateTerminalProfiles(terminalProfilesViewModel: TerminalProfilesViewModel, retailerId: string): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`terminalConfig/ESocket/TerminalProfiles/${retailerId}`, terminalProfilesViewModel);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    } else if (httpResult.error["status"] == 403) {
      return { message: "You do not have the permission to update a terminal config. Please contact system administrator.", success: false }
    }
    return { message: "An unknown error occured", success: false };
  }

  public async GetConfigIdSuffixItems(retailerId: string): Promise<ConfigSetViewModel[]> {
    const httpResult = await this._httpService.apiGet<ConfigSetViewModel[]>(`terminalConfig/GetConfigSets/${retailerId}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetConfigIdSuffix(retailerId: string): Promise<ConfigSetViewModel> {
    const httpResult = await this._httpService.apiGet<ConfigSetViewModel>(`terminalConfig/GetConfigSet/${retailerId}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return null;
  }

  public async GetPropertyProfile(retailerId: string): Promise<PropertyProfileViewModel>{
    const httpResult = await this._httpService.apiGet<PropertyProfileViewModel>(`terminalConfig/GetPropertyProfile/${retailerId}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return;
  }

  public async GetRegions(): Promise<RegionConfigViewModel[]> {
    const httpResult = await this._httpService.apiGet<RegionConfigViewModel[]>(`terminalConfig/GetRegions`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetCountries(): Promise<CountryConfigViewModel[]> {
    const httpResult = await this._httpService.apiGet<CountryConfigViewModel[]>(`terminalConfig/GetCountries`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetRolloutGroupItems(retailerId: string): Promise<RolloutGroupViewModel[]> {
    const httpResult = await this._httpService.apiGet<RolloutGroupViewModel[]>(`terminalConfig/GetRolloutGroups/${retailerId}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetRetailersByParticipant(participantId: number): Promise<RetailerConfigViewModel[]> {
    const httpResult = await this._httpService.apiGet<RetailerConfigViewModel[]>(`terminalConfig/GetRetailersByParticipant/${participantId}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return null;
  }

  public async GetRetailer(participantId: number, terminalConfigId: number): Promise<RetailerConfigViewModel> {
    const httpResult = await this._httpService.apiGet<RetailerConfigViewModel>(`terminalConfig/GetRetailer/${participantId}/${terminalConfigId}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return null;
  }

  public async GetStoreByRetailerId(retailerId: string): Promise<StoreViewModel[]> {
    const httpResult = await this._httpService.apiGet<StoreViewModel[]>(`terminalConfig/GetStoreByRetailerId/${retailerId}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return null;
  }

  public async GetLaneProfileByName(laneProfileName: string): Promise<LaneProfilesViewModel> {
    const httpResult = await this._httpService.apiGet<LaneProfilesViewModel>(`terminalConfig/GetLaneProfileByName/${laneProfileName}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return null;
  }
    
  public async GetTerminalProfileByTermProf(termProfileName: string): Promise<TerminalProfilesViewModel> {
    debugger;
    const httpResult = await this._httpService.apiGet<TerminalProfilesViewModel>(`terminalConfig/GetTerminalProfileByTermProf/${termProfileName}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return null;
  }

  public async GetTerminalProfilesByRetailer(retailerId: string): Promise<TerminalProfilesViewModel[]> {
    const httpResult = await this._httpService.apiGet<TerminalProfilesViewModel[]>(`terminalConfig/GetTerminalProfilesByRetailerId/${retailerId}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return null;
  }

  public async DeleteStore(store: StoreViewModel): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`terminalConfig/ESocket/DeleteStore`, store);
    if (httpResult && httpResult.body) {
      this.InitTermConfigCount();
      return httpResult.body;
    }
    else if (httpResult.error["status"] == 403) {
      return { message: "You do not have the permission to delete terminal config. Please contact system administrator.", success: false }
    }
    return { message: "An unknown error occured", success: false };
  }

  public async DeleteLaneProfile(laneProfile: LaneProfilesViewModel): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`terminalConfig/ESocket/DeleteLaneProfile`, laneProfile);
    if (httpResult && httpResult.body) {
      this.InitTermConfigCount();
      return httpResult.body;
    }
    else if (httpResult.error["status"] == 403) {
      return { message: "You do not have the permission to delete terminal config. Please contact system administrator.", success: false }
    }
    return { message: "An unknown error occured", success: false };
  }

  public async GetLaneProfilesByRetailer(retailerId: string): Promise<LaneProfilesViewModel[]>{
    const httpResult = await this._httpService.apiGet<LaneProfilesViewModel[]>(`terminalConfig/GetLaneProfilesByRetailerId/${retailerId}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return null;
  }

  public async GetAdditionalEsocketConfig(participantId: number, terminalConfigId: number): Promise<AdditionalEsocketConfig> {
    const httpResult = await this._httpService.apiGet<AdditionalEsocketConfig>(`terminalConfig/GetAdditionalEsocketConfig/${participantId}/${terminalConfigId}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return null;
  }

  public async GetStore(storeId: string): Promise<StoreViewModel> {
    const httpResult = await this._httpService.apiGet<StoreViewModel>(`terminalConfig/GetStore/${storeId}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return null;
  }

  public async GetRolloutGroup(retailerId: string): Promise<RolloutGroupViewModel> {
    const httpResult = await this._httpService.apiGet<RolloutGroupViewModel>(`terminalConfig/GetRolloutGroup/${retailerId}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return null;
  } 

  public async GetBranchItems(): Promise<BranchViewModel[]> {
    const httpResult = await this._httpService.apiGet<BranchViewModel[]>(`terminalConfig/GetBranches`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetAidClassItems(branch: string): Promise<AidClassViewModel[]> {
    const httpResult = await this._httpService.apiGet<AidClassViewModel[]>(`terminalConfig/GetAidClasses/${branch}`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetCardSets(branch: string): Promise<CardSetConfigServerViewModel[]> {

    const httpResult = await this._httpService.apiGet<CardSetConfigServerViewModel[]>(`terminalConfig/cardSets/${branch}`);

    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }


  public async GetCountryCodes(): Promise<CountryViewModel[]> {
    const httpResult = await this._httpService.apiGet<CountryViewModel[]>(`terminalConfig/CountryCodes`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetTerminalTypes(): Promise<TerminalTypeViewModel[]> {
    const httpResult = await this._httpService.apiGet<TerminalTypeViewModel[]>(`terminalConfig/terminaltypes`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetBinProfiles(): Promise<BinProfilesViewModel[]> {

    const httpResult = await this._httpService.apiGet<BinProfilesViewModel[]>(`terminalConfig/BinProfiles`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetTimeZoneCodes(): Promise<TimeZoneCodeViewModel[]> {
    const httpResult = await this._httpService.apiGet<TimeZoneCodeViewModel[]>(`terminalConfig/TimeZoneCodes`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetSupportTeams(): Promise<SupportTeamsViewModel[]> {
    const httpResult = await this._httpService.apiGet<SupportTeamsViewModel[]>(`terminalConfig/SupportTeams`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async GetTermDriverID(): Promise<TermDriverIDViewModel[]> {
    const httpResult = await this._httpService.apiGet<TermDriverIDViewModel[]>(`terminalConfig/TermDriverID`);
    if (httpResult && httpResult.body) {
      return httpResult.body;
    }
    return [];
  }

  public async DeleteTerminalConfig(configId: number): Promise<CreateResult> {
    const httpResult = await this._httpService.apiPost<CreateResult>(`terminalConfig/${configId}/DeleteTerminalConfig`, configId);
    if (httpResult && httpResult.body) {
      this.InitTermConfigCount();
      return httpResult.body;
    }
    else if (httpResult.error["status"] == 403) {
      return { message: "You do not have the permission to delete terminal config. Please contact system administrator.", success: false }
    }
    return { message: "An unknown error occured", success: false };
  }

}
